/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Typography, withStyles, withTheme } from '@material-ui/core';
import { FilteringState, IntegratedFiltering, IntegratedSelection, SelectionState } from '@devexpress/dx-react-grid';
import { Grid, Table, TableFilterRow, TableHeaderRow, TableSelection } from '@devexpress/dx-react-grid-material-ui';
import { IntegratedSorting, SortingState } from '@devexpress/dx-react-grid';
import { compose } from 'recompose';
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import { formatDateGeneric } from 'utilities/common';
import { formatMoney } from 'utilities/currencyFormatter';
import Log from 'utilities/log';
import Spinner from 'modules/common/spinner.component';
import { updateFeeStatuses } from './fees.actions';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import { UPDATE_FEE_STATUSES_FAILURE } from './fees.types';
import { TOAST_MESSAGE_SEVERITY_ERROR } from '../layout/types';
import ChangeFeeStatusDialog from './changeFeeStatusDialog.component';

const styles = theme => {
  const intakeRecordBorder = `1px solid ${theme.palette.grey[500]}`;
  return {
    table: { borderSpacing: theme.spacing(0, 2) },
    tableHead: { height: 32 },
    cell: {
      borderTop: intakeRecordBorder,
      borderBottom: intakeRecordBorder,
      borderLeft: 0,
      borderRight: 0,
      '&:first-child': {
        borderTopLeftRadius: theme.shape.borderRadius,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderLeft: intakeRecordBorder
      },
      '&:last-child': {
        borderTopRightRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        borderRight: intakeRecordBorder
      }
    },
    row: { backgroundColor: theme.palette.common.white },
    noDataCell: { paddingBottom: theme.spacing(12), paddingTop: theme.spacing(12), textAlign: 'center' },
    modal: { padding: theme.spacing(4) },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      paddingTop: theme.spacing(2),
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: '16px'
    }
  };
};

const FILTER_DISABLED_COLUMNS = ['organization'];
const getRowId = row => row.feeRecordId;

class FeesGrid extends PureComponent {
  static defaultProps = { isLoading: false };

  getSelectedRows = selection => this.props.fees.filter(fee => selection.includes(fee.feeRecordId));

  constructor(props) {
    super(props);
    this.state = {
      columns: [
        { name: 'clientId', title: 'Client ID', getCellValue: r => r.clientId },
        { name: 'authorizationFileId', title: 'Authorization File ID', getCellValue: r => r.authorizationFileId },
        { name: 'eeFullName', title: 'Name', getCellValue: r => r.eeFullName },
        {
          name: 'isConfidentialFile',
          title: 'Confidential File',
          getCellValue: r => (r.isConfidentialFile ? <Typography style={{ fontWeight: 'bold' }}>Yes</Typography> : 'No')
        },
        { name: 'billingPolicyName', title: 'Billing Policy', getCellValue: r => r.billingPolicyName },
        { name: 'relocationPolicyName', title: 'Relocation Policy', getCellValue: r => r.relocationPolicyName },
        { name: 'fileType', title: 'File Type', getCellValue: r => r.fileType },
        { name: 'fileStatus', title: 'File Status', getCellValue: r => r.fileStatus },
        {
          name: 'feeAmount',
          title: 'Amount',
          getCellValue: r => <Typography>{r.feeAmount == null ? '' : formatMoney(r.feeAmount)}</Typography>
        },
        { name: 'fileInitDate', title: 'Init Date', getCellValue: r => formatDateGeneric(r.fileInitDate) },
        { name: 'fileCanceledDate', title: 'Canceled Date', getCellValue: r => formatDateGeneric(r.fileCanceledDate) },
        { name: 'fileCompletedDate', title: 'Completed Date', getCellValue: r => formatDateGeneric(r.fileCompletedDate) },
        {
          name: 'priorFeeStatusId',
          title: 'Origin Status',
          getCellValue: r => (
            <Typography>
              {this.props.enumerations ? this.props.enumerations.ownershipTypes.find(s => s.id === r.priorFeeStatusId)?.title : ''}
            </Typography>
          )
        }
      ],
      filters: [],
      sorting: [],
      integratedFilteringColumnExtensions: [],
      tableColumnExtensions: [],
      selection: [],
      selectedRows: [],
      updateStatusId: '',
      updateStatusReasonId: '',
      updateStatusOtherReason: '',
      showChangeStatuses: false,
      showChangeStatusModal: false,
      updatingStatuses: false
    };
    this.changeSorting = sorting => this.setState({ sorting });
  }

  changeFilters = filters => this.setState({ filters });

  filterCell = props =>
    props.column.title && !FILTER_DISABLED_COLUMNS.includes(props.column.name) ? <TableFilterRow.Cell {...props} /> : null;

  headerCell = props => (props.column.title ? <TableHeaderRow.Cell {...props} /> : null);

  headerRowComponent = props => <TableHeaderRow.Row className={this.props.classes.tableHead} {...props} />;

  rowComponent = props => <Table.Row className={this.props.classes.row} {...props} />;

  cellClicked = feeRecordId => {
    this.props.history.push(`/fees/${feeRecordId}`);
  };

  cellComponent = props => {
    let clickHandler = () => this.cellClicked(props.row.feeRecordId);
    const className = classNames(this.props.classes.cell, 'handles-click');
    return <Table.Cell onClick={clickHandler} className={className} {...props} />;
  };

  saveSelected = async (updateStatusId, updateStatusReasonId, updateStatusOtherReason) => {
    let newValues = [];

    this.state.selectedRows.forEach(s => {
      newValues.push({
        feeRecordId: s.feeRecordId,
        feeStatusId: updateStatusId,
        feeStatusReasonId: updateStatusReasonId && updateStatusReasonId !== '' ? updateStatusReasonId : null,
        feeStatusReasonComment: updateStatusOtherReason,
        modifiedBy: 0,
        modifiedOn: new Date()
      });
    });

    let resp = await this.props.updateFeeStatuses(newValues);
    if (resp.type === UPDATE_FEE_STATUSES_FAILURE) {
      this.props.showToast('Failed to update fee statuses, please try again', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } else {
      // the ID of the row is set to the value to save for onChange

      this.setState({ selection: [] });
      this.setState({ selectedRows: [] });
      this.setState({ updateStatusId: '' });
      this.setState({ updateStatusReasonId: '' });
      this.setState({ updateStatusOtherReason: '' });
      this.setState({ showChangeStatusesModal: false }); //required to exit the searching modal and close it
      this.setState({ showChangeStatuses: false }); //required to exit the searching modal and close it
    }
  };

  closeModal = () => {
    this.props.handleChange({ showChangeStatusesModal: false });
  };

  tableComponent = props => <Table.Table className={this.props.classes.table} {...props} />;

  noDataCellComponent = props => {
    const content = this.props.isLoading ? <Spinner logo /> : 'No Records were returned.  Check the filters above.';
    const { getMessage, ...rest } = props;
    return (
      <Table.Cell {...rest} className={this.props.classes.noDataCell}>
        <Typography>{content}</Typography>
      </Table.Cell>
    );
  };

  render() {
    Log.trace('RENDER', 'feesGrid');
    const { fees, classes, feeStatuses, feeStatusReasons } = this.props;
    const {
      columns,
      filters,
      tableColumnExtensions,
      selectedRows,
      updatingStatuses,
      integratedFilteringColumnExtensions,
      sorting,
      selection,
      showChangeStatusesModal,
      showChangeStatuses
    } = this.state;
    // let filteredStatusReasons = feeStatusReasons?.filter(option => option.additionalField.toLowerCase() === updateStatusId || option.additionalField.toLowerCase() === '00000000-0000-0000-0000-000000000000') || [];

    if (updatingStatuses) {
      if (selection.length > 0) {
        this.setState({ showChangeStatuses: true });
      } else {
        this.setState({ showChangeStatuses: false });
      }
      this.setState({ selectedRows: this.getSelectedRows(this.state.selection) });
      this.setState({ updatingStatuses: false });
    }

    return (
      <div>
        {showChangeStatuses && (
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <Box sx={{ padding: '10px', display: 'flex', flexDirection: 'row' }}></Box>
            <Button
              color="secondary"
              variant="contained"
              sx={{
                maxHeight: '40px',
                marginRight: '10px',
                marginTop: '8px',
                backgroundColor: '#5D9878',
                '&:hover': { backgroundColor: '#528569' }
              }}
              onClick={() => this.setState({ showChangeStatusesModal: true })}
            >
              {' '}
              Change Status{' '}
            </Button>
          </Box>
        )}
        <Grid rows={fees} columns={columns} getRowId={getRowId}>
          <SortingState sorting={sorting} onSortingChange={this.changeSorting} />
          <FilteringState filters={filters} onFiltersChange={this.changeFilters} />
          <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />
          <IntegratedSorting />
          <SelectionState
            selection={selection}
            onSelectionChange={e => {
              const nextSelection = e.filter(selectedFeeRecordId => selectedFeeRecordId !== undefined);
              this.setState({ selection: nextSelection });
              this.setState({ updatingStatuses: true });
            }}
          />
          <IntegratedSelection />
          <Table
            columnExtensions={tableColumnExtensions}
            rowComponent={this.rowComponent}
            tableComponent={this.tableComponent}
            cellComponent={this.cellComponent}
            noDataCellComponent={this.noDataCellComponent}
          />
          <TableHeaderRow rowComponent={this.headerRowComponent} cellComponent={this.headerCell} showSortingControls />
          <TableFilterRow cellComponent={this.filterCell} />
          <TableSelection showSelectAll highlightRow selectByRowClick />
        </Grid>
        <ChangeFeeStatusDialog
          open={showChangeStatusesModal}
          onCancel={() => this.setState({ showChangeStatusesModal: false })}
          onSave={this.saveSelected}
          feeStatusReasons={feeStatusReasons}
          feeStatuses={feeStatuses}
          selectedRows={selectedRows}
          columns={columns}
          sorting={sorting}
          filters={filters}
          integratedFilteringColumnExtensions={integratedFilteringColumnExtensions}
          tableColumnExtensions={tableColumnExtensions}
          classes={classes}
          rowComponent={this.rowComponent}
          tableComponent={this.tableComponent}
          cellComponent={this.cellComponent}
          noDataCellComponent={this.noDataCellComponent}
          headerRowComponent={this.headerRowComponent}
          headerCell={this.headerCell}
          filterCell={this.filterCell}
          changeSorting={this.changeSorting}
          changeFilters={this.changeFilters}
        />
        {/* <Dialog open={showChangeStatusesModal} fullWidth={false} maxWidth={'lg'} sx={{padding: '20px'}}>
          <div className={classes.modal} sx={{padding: '100px'}} styles={{padding: '100px'}} >
            <Typography variant="h6" sx={{ padding: '10px'}}>Change Status of Selected Records?</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', gap: '24px', px: '10px', flexWrap: 'nowrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '8px' }}>
                <Typography>Fee Status:</Typography>
                <TextField
                    select
                    label="Status"
                    className={classes.textField}
                    value={updateStatusId}
                    onChange={e => {this.setState({ updateStatusId: e.target.value }); filteredStatusReasons = (feeStatusReasons || []).filter(option => option.additionalField.toLowerCase() === e.target.value.toLowerCase() || option.additionalField.toLowerCase() === '00000000-0000-0000-0000-000000000000');}}
                    SelectProps={{ autoWidth: true }}
                    variant="outlined"
                  >
                    {feeStatuses.map(option => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                { (updateStatusId === 'a8aa34cf-06e0-431b-8411-45fca666fbfd' || updateStatusId === 'ceda815f-8eb7-47c2-8a38-9bd684e1b574') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', }}>
                  <Typography>Status Reason:</Typography>
                  <TextField
                      select
                      label="Status Reason"
                      className={classes.textField}
                      value={updateStatusReasonId}
                      onChange={e => {this.setState({ updateStatusReasonId: e.target.value })}}
                      SelectProps={{ autoWidth: true }}
                      variant="outlined"
                      xs={{ minWidth: '200px' }}
                    >
                      {filteredStatusReasons.map(option => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.title}
                        </MenuItem>
                      ))}
                    </TextField>
                </Box>
              )}
              { (updateStatusReasonId === '22411a19-ee33-4cd7-90d3-bc36d461a73c') && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', }}>
                  <Typography>Other Reason:</Typography>
                  <TextField
                      text
                      label="Other Reason"
                      className={classes.textField}
                      value={updateStatusOtherReason}
                      onChange={e => {this.setState({ updateStatusOtherReason: e.target.value })}}
                      SelectProps={{ autoWidth: true }}
                      variant="outlined"
                      xs={{ minWidth: '200px' }}
                    >
                    </TextField>
                </Box>
              )}
            </Box>
            <Paper>
                <Grid rows={selectedRows} columns={columns}>
                  <SortingState sorting={sorting} onSortingChange={this.changeSorting} />
                  <FilteringState filters={filters} onFiltersChange={this.changeFilters} />
                  <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />
                  <IntegratedSorting />
                  <Table
                    columnExtensions={tableColumnExtensions}
                    rowComponent={this.rowComponent}
                    tableComponent={this.tableComponent}
                    cellComponent={this.cellComponent}
                    noDataCellComponent={this.noDataCellComponent}
                  />
                  <TableHeaderRow rowComponent={this.headerRowComponent} cellComponent={this.headerCell} showSortingControls />
                  <TableFilterRow cellComponent={this.filterCell} />
                </Grid>
              </Paper>
            
            <div className={classes.buttonContainer} >
             
              <Button variant="contained" color="secondary" onClick={() => this.setState({ showChangeStatusesModal: false })} sx={{ marginRight: '10px', backgroundColor: '#D95C5C', '&:hover': { backgroundColor: '#B34747' } }}>
                Cancel
              </Button>
               <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  this.saveSelected();
                }}
                sx={{ marginRight: '10px', backgroundColor: '#5D9878', '&:hover': { backgroundColor: '#528569' } }}
              >
                Save
              </Button>
            </div>
          </div>
        </Dialog> */}
      </div>
    );
  }
}

export default compose(withStyles(styles), withTheme, connect(null, { updateFeeStatuses, showToast }))(FeesGrid);
